import React, { useRef, useCallback } from 'react';

import { useProcessAsync } from '@gogame/hooks-ts';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  useCreate,
  useIsUpdating,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  useUploader,
  useFileMetadata,
} from 'scenes/Gallery/components/Uploader';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

import CreateConceptForm from './CreateConceptForm';
import { getValidationSchema } from './schemas';

const CreateConcept = ({
  file,
  initialValues = {},
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const [loading, processAsync] = useProcessAsync();

  const promiseRef = useRef([]);

  const handleProcessAsync = useCallback((p) => {
    promiseRef.current = [...promiseRef.current, p];
    return () => {
      promiseRef.current = promiseRef.current.filter((c) => c !== p);
    };
  }, []);

  const fileUpload = useUploader({
    file,
    processAsync: handleProcessAsync,
  });

  const fileMetadata = useFileMetadata({
    file,
  });

  const [createConcept] = useCreate();
  const isUpdating = useIsUpdating();
  const {
    isAllowConceptFamilies,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowBusinessPartner,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();
  const handleSubmit = async (values) => {
    const [fileGuid] = await Promise.all(
      promiseRef.current.map((p) => processAsync(p))
    );
    const {
      [CONCEPT.ORIGINAL]: original,
      [CONCEPT.JIRA_TASK]: jiraTask,
      [CONCEPT.REQUESTED_BY]: requestedBy,
      [CONCEPT.BUSINESS_PARTNERS]: businessPartners,
      ...concept
    } = values;

    original[CREATIVE.FILE_GUID] = fileGuid;
    original[CREATIVE.DESCRIPTION] = concept[CONCEPT.DESCRIPTION];
    original[CREATIVE.INHERIT_TAGS] = concept[CONCEPT.TAGS];
    original[CREATIVE.TAGS] = concept[CONCEPT.TAGS];

    if (isAllowJiraTask) {
      concept[CONCEPT.JIRA_TASK] = jiraTask;
      original[CREATIVE.JIRA_TASK] = jiraTask;
    }
    if (isAllowRequestedBy) {
      concept[CONCEPT.REQUESTED_BY] = requestedBy;
      original[CREATIVE.REQUESTED_BY] = requestedBy;
    }
    if (isAllowBusinessPartner) {
      concept[CONCEPT.BUSINESS_PARTNERS] = businessPartners;
      original[CREATIVE.BUSINESS_PARTNERS] = businessPartners;
    }

    const conceptId = await createConcept({
      ...concept,
      [CONCEPT.ORIGINAL]: original,
    });
    onSubmit({ id: conceptId });
  };

  return (
    <CreateConceptForm
      loading={loading || isUpdating}
      validationSchema={getValidationSchema({
        isAllowConceptFamilies,
        isAllowRequestedBy,
        isAllowJiraTask,
        isAllowBusinessPartner,
        isAllowTested,
        isAllowProductionType,
      })}
      title={'Add Concept'}
      onClose={onClose}
      open={open}
      onExited={onExited}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      fileInfo={{
        ...fileUpload,
        ...fileMetadata,
      }}
    />
  );
};
export default CreateConcept;
